/** Shapes the box / cylinder / gable primitives cannot make: swept wings, fins, broken slabs. */

import { type Color, Vector3 } from 'three';
import type { MeshBuilder } from './MeshBuilder';

export interface PrismColors {
  top: Color;
  side: Color;
  /** Defaults to `side`. */
  bottom?: Color;
}

/**
 * Extrude a CONVEX outline of (x, z) points from `y0` up to `y1`; either winding will do.
 * For an upright shape (a tail fin) rotate the builder's frame first: under
 * `place(0, 0, 0, 0, 1, 0, Math.PI / 2)` outline x becomes height and the extrusion runs along -x.
 */
export function prism(
  mb: MeshBuilder,
  outline: ReadonlyArray<readonly [x: number, z: number]>,
  y0: number,
  y1: number,
  colors: PrismColors,
): void {
  // Shoelace area in (x, z): negative is the winding whose fan faces +y.
  let area = 0;
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    if (a && b) area += a[0] * b[1] - b[0] * a[1];
  }
  const pts = area > 0 ? [...outline].reverse() : [...outline];
  const at = (i: number, y: number): Vector3 => {
    const q = pts[i % pts.length];
    return new Vector3(q ? q[0] : 0, y, q ? q[1] : 0);
  };
  for (let i = 1; i < pts.length - 1; i++) {
    mb.tri(at(0, y1), at(i, y1), at(i + 1, y1), colors.top);
    mb.tri(at(0, y0), at(i + 1, y0), at(i, y0), colors.bottom ?? colors.side);
  }
  for (let i = 0; i < pts.length; i++) mb.quad(at(i, y0), at(i + 1, y0), at(i + 1, y1), at(i, y1), colors.side);
}

/** Mirror an outline across the z axis (x -> -x): the other wing. */
export function mirrorX(outline: ReadonlyArray<readonly [number, number]>): Array<[number, number]> {
  return outline.map(([x, z]) => [-x, z]);
}
